<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">
				회의실 · 법인차 예약
				<div class="right">
					<button type="button" class="btn btn-sm btn-primary" @click="openAdd(null, null)">예약 등록</button>
				</div>
			</div>

			<div class="search-top">
				<div class="left">
					<label class="input-label">구분</label>
					<select v-model="kind" class="form-control select sm" @change="load">
						<option value="room">회의실</option>
						<option value="car">법인차</option>
					</select>
					<button type="button" class="btn btn-sm btn-light" @click="moveDate(-1)"><b-icon-chevron-left /></button>
					<input type="date" class="form-control sm" v-model="date" @change="load" />
					<button type="button" class="btn btn-sm btn-light" @click="moveDate(1)"><b-icon-chevron-right /></button>
					<button type="button" class="btn btn-sm btn-secondary" @click="goToday">오늘</button>
					<span class="m-l--1 txt-c--grey">{{ rangeLabel }}</span>
				</div>
				<div class="right">
					<div class="tab-btn sm">
						<button type="button" class="btn tab" v-bind:class="{ active: view === 'day' }" @click="setView('day')">
							일간
						</button>
						<button type="button" class="btn tab" v-bind:class="{ active: view === 'week' }" @click="setView('week')">
							주간
						</button>
						<button type="button" class="btn tab" v-bind:class="{ active: view === 'month' }" @click="setView('month')">
							월간
						</button>
						<button type="button" class="btn tab" v-bind:class="{ active: view === 'mine' }" @click="setView('mine')">
							내 예약
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- ── R-6 일간 타임라인 (자원 × 시간대 그리드) ────────────────────────── -->
		<div class="section" v-if="view === 'day'">
			<div class="section-tit sub">{{ date }} {{ dowText(date) }} · {{ kindLabel }} 타임라인</div>
			<div class="table-wrap bk-scroll">
				<table class="table bk-grid">
					<colgroup>
						<col width="120" />
						<col v-for="s in slots" :key="'cg_' + s" />
					</colgroup>
					<tr>
						<th>{{ kindLabel }}</th>
						<th v-for="s in slots" :key="'hh_' + s" class="bk-grid--head">
							{{ s.endsWith(':00') ? s.slice(0, 2) : '' }}
						</th>
					</tr>
					<tr v-for="r in resources" :key="'res_' + r.booking_resource_uid">
						<th class="txt-left">
							{{ r.name }}
							<span v-if="r.res_no" class="txt-c--grey d-block">{{ r.res_no }}</span>
						</th>
						<template v-for="(s, si) in slots" :key="'cell_' + r.booking_resource_uid + '_' + s">
							<td
								v-if="!coveredBySpan(r.booking_resource_uid, date, si)"
								:colspan="spanAt(r.booking_resource_uid, date, si)"
								v-bind:class="cellClass(r.booking_resource_uid, date, si)"
								@click="onCellClick(r, date, si)"
							>
								<span v-if="bookingAt(r.booking_resource_uid, date, si)" class="bk-grid--label">
									{{ bookingAt(r.booking_resource_uid, date, si).stime }}~{{
										bookingAt(r.booking_resource_uid, date, si).etime
									}}
									{{ bookingAt(r.booking_resource_uid, date, si).reg_mem_name }}
									<template v-if="bookingAt(r.booking_resource_uid, date, si).purpose">
										/ {{ bookingAt(r.booking_resource_uid, date, si).purpose }}
									</template>
								</span>
							</td>
						</template>
					</tr>
					<tr v-if="!resources.length">
						<td :colspan="slots.length + 1">등록된 {{ kindLabel }}이 없습니다. 시스템관리에서 등록하세요.</td>
					</tr>
				</table>
			</div>
			<div class="txt-c--grey mt-1">
				※ 빈 칸을 클릭하면 그 시간으로 예약 등록 창이 열립니다. 예약 블록을 클릭하면 상세(연장 · 취소)가 열립니다.
				(타임라인 표시 구간 {{ slots[0] }} ~ {{ gridEndLabel }})
			</div>
			<!-- 표시 구간(08~22시) 밖의 예약은 격자에 자리가 없다 → 조용히 감추지 않고 따로 안내한다. -->
			<div v-if="offGrid.length" class="txt-c--red mt-1">
				표시 구간 밖 예약 {{ offGrid.length }}건:
				<button
					v-for="b in offGrid"
					:key="'og_' + b.booking_uid"
					type="button"
					class="btn btn-xsm btn-light m-r--1"
					@click="openItem(b)"
				>
					{{ b.res_name }} {{ b.stime }}~{{ b.etime }} {{ b.reg_mem_name }}
				</button>
			</div>
		</div>

		<!-- ── R-6 주간 타임라인 (자원별 요일 × 예약) ─────────────────────────── -->
		<div class="section" v-else-if="view === 'week'">
			<div class="section-tit sub">{{ rangeLabel }} · {{ kindLabel }} 주간</div>
			<div class="table-wrap">
				<table class="table bk-week">
					<colgroup>
						<col width="14%" />
						<col v-for="d in weekDays" :key="'wc_' + d" />
					</colgroup>
					<tr>
						<th>{{ kindLabel }}</th>
						<th v-for="d in weekDays" :key="'wh_' + d" v-bind:class="{ 'bk-today': d === todayStr }">
							{{ Number(d.slice(5, 7)) }}/{{ Number(d.slice(8)) }} {{ dowText(d) }}
						</th>
					</tr>
					<tr v-for="r in resources" :key="'wr_' + r.booking_resource_uid">
						<th class="txt-left">{{ r.name }}</th>
						<td v-for="d in weekDays" :key="'wd_' + r.booking_resource_uid + '_' + d" class="bk-week--cell">
							<button
								v-for="b in bookingsOf(r.booking_resource_uid, d)"
								:key="'wb_' + b.booking_uid"
								type="button"
								class="btn btn-xsm bk-week--ev"
								v-bind:class="b.reg_mem_uid == myUid ? 'btn-primary' : 'btn-secondary'"
								@click="openItem(b)"
							>
								{{ b.stime }}~{{ b.etime }} {{ b.reg_mem_name }}
							</button>
							<button
								type="button"
								class="btn btn-xsm btn-light bk-week--add"
								@click="openAdd(r, d)"
							>
								+
							</button>
						</td>
					</tr>
					<tr v-if="!resources.length">
						<td :colspan="weekDays.length + 1">등록된 {{ kindLabel }}이 없습니다.</td>
					</tr>
				</table>
			</div>
		</div>

		<!-- ── R-6 월간 캘린더 ────────────────────────────────────────────────── -->
		<div class="section" v-else-if="view === 'month'">
			<div class="section-tit sub">{{ date.slice(0, 7) }} · {{ kindLabel }} 월간</div>
			<div class="table-wrap">
				<table class="table bk-cal">
					<tr>
						<th v-for="(d, i) in DOW" :key="'mh_' + i" v-bind:class="i === 0 ? 'txt-c--red' : ''">{{ d }}</th>
					</tr>
					<tr v-for="(week, wi) in calWeeks" :key="'mw_' + wi">
						<td v-for="(day, di) in week" :key="'md_' + wi + '_' + di" class="bk-cal--cell">
							<div v-if="day" class="bk-cal--day" v-bind:class="{ today: day === todayStr }">
								<span v-bind:class="di === 0 ? 'txt-c--red' : ''">{{ Number(day.slice(8)) }}</span>
							</div>
							<div v-if="day">
								<button
									v-for="b in bookingsOfDate(day)"
									:key="'mb_' + b.booking_uid"
									type="button"
									class="btn btn-xsm bk-cal--ev"
									v-bind:class="b.reg_mem_uid == myUid ? 'btn-primary' : 'btn-secondary'"
									@click="openItem(b)"
								>
									{{ b.stime }} {{ b.res_name }} {{ b.reg_mem_name }}
								</button>
							</div>
						</td>
					</tr>
				</table>
			</div>
		</div>

		<!-- ── R-6 내 예약 ────────────────────────────────────────────────────── -->
		<div class="section" v-else>
			<div class="section-tit sub">내 예약 ({{ date.slice(0, 7) }}) — 취소 내역 포함</div>
			<div class="table-wrap">
				<table class="table">
					<colgroup>
						<col width="12%" /><col width="14%" /><col width="10%" /><col width="14%" />
						<col width="20%" /><col width="10%" /><col width="20%" />
					</colgroup>
					<tr>
						<th>예약일자</th>
						<th>대상</th>
						<th>구분</th>
						<th>이용시간</th>
						<th>목적</th>
						<th>상태</th>
						<th>관리</th>
					</tr>
					<tr v-for="b in list" :key="'my_' + b.booking_uid">
						<td>{{ b.book_date }} {{ dowText(b.book_date) }}</td>
						<td>{{ b.res_name }}</td>
						<td>{{ b.res_kind === 'car' ? '법인차' : '회의실' }}</td>
						<td>{{ b.stime }} ~ {{ b.etime }}</td>
						<td class="txt-left">{{ b.purpose }}</td>
						<td>
							<span v-if="b.status === 'cancel'" class="txt-c--red">취소</span>
							<span v-else class="txt-c--blue">예약</span>
							<span v-if="b.extend_cnt > 0" class="txt-c--grey d-block">{{ b.extend_cnt }}회 연장</span>
						</td>
						<td>
							<template v-if="b.status === 'ok' && b.can_edit">
								<button type="button" class="btn btn-xsm btn-secondary" @click="openItem(b)">연장/취소</button>
							</template>
							<span v-else-if="b.cancel_reason" class="txt-c--grey">{{ b.cancel_reason }}</span>
						</td>
					</tr>
					<tr v-if="!list.length">
						<td colspan="7">이 달의 내 예약이 없습니다.</td>
					</tr>
				</table>
			</div>
		</div>

		<!-- ── R-3 예약 등록 팝업 (카톡 양식 그대로) ──────────────────────────── -->
		<div v-if="addPopup" class="popup-wrap">
			<div class="dim" @click="addPopup = false"></div>
			<div class="popup sm">
				<div class="popup-tit">
					{{ kind === 'car' ? '차량 예약' : '회의실 예약' }}
					<button type="button" class="btn btn-close" @click="addPopup = false"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<table class="table type2">
						<colgroup>
							<col width="32%" /><col width="68%" />
						</colgroup>
						<tr>
							<th>{{ kind === 'car' ? '차량' : '회의실' }} <span class="txt-c--red">*</span></th>
							<td>
								<select v-model="addForm.resUid" class="form-control select">
									<option :value="null">선택</option>
									<option v-for="r in resources" :key="'ar_' + r.booking_resource_uid" :value="r.booking_resource_uid">
										{{ r.name }}{{ r.res_no ? ' (' + r.res_no + ')' : '' }}
									</option>
								</select>
							</td>
						</tr>
						<tr>
							<th>예약일자 <span class="txt-c--red">*</span></th>
							<td>
								<input type="date" class="form-control" v-model="addForm.book_date" />
								<span class="txt-c--grey">{{ dowText(addForm.book_date) }}</span>
							</td>
						</tr>
						<tr>
							<th>이용시간 <span class="txt-c--red">*</span></th>
							<td><input type="time" class="form-control" v-model="addForm.stime" step="600" /></td>
						</tr>
						<tr>
							<th>{{ kind === 'car' ? '반납시간' : '종료시간' }} <span class="txt-c--red">*</span></th>
							<td><input type="time" class="form-control" v-model="addForm.etime" step="600" /></td>
						</tr>
						<tr>
							<th>{{ kind === 'car' ? '사용목적' : '이용목적' }}</th>
							<td><input type="text" class="form-control" v-model="addForm.purpose" maxlength="200" /></td>
						</tr>
						<tr>
							<th>예약자</th>
							<td>{{ myName }}</td>
						</tr>
					</table>
					<div class="txt-c--grey mt-1">※ 같은 {{ kindLabel }}의 시간이 겹치면 저장되지 않고 기존 예약자를 안내합니다.</div>
				</div>
				<div class="popup-btn">
					<button type="button" class="btn btn-light" @click="addPopup = false">취소</button>
					<button type="button" class="btn btn-primary" @click="submitAdd" :disabled="saving">예약</button>
				</div>
			</div>
		</div>

		<!-- ── R-5 상세 / 연장 / 취소 팝업 ────────────────────────────────────── -->
		<div v-if="itemPopup && current" class="popup-wrap">
			<div class="dim" @click="itemPopup = false"></div>
			<div class="popup sm">
				<div class="popup-tit">
					예약 상세
					<button type="button" class="btn btn-close" @click="itemPopup = false"><b-icon-x-lg /></button>
				</div>
				<div class="popup-con">
					<table class="table type2">
						<colgroup>
							<col width="32%" /><col width="68%" />
						</colgroup>
						<tr>
							<th>대상</th>
							<td>{{ current.res_name }} {{ current.res_no ? '(' + current.res_no + ')' : '' }}</td>
						</tr>
						<tr>
							<th>예약일자</th>
							<td>{{ current.book_date }} {{ dowText(current.book_date) }}</td>
						</tr>
						<tr>
							<th>이용시간</th>
							<td>{{ current.stime }} ~ {{ current.etime }}</td>
						</tr>
						<tr>
							<th>목적</th>
							<td>{{ current.purpose || '-' }}</td>
						</tr>
						<tr>
							<th>예약자</th>
							<td>{{ current.reg_mem_name }} {{ current.reg_mem_rank_name }}</td>
						</tr>
						<tr>
							<th>상태</th>
							<td>
								<span v-if="current.status === 'cancel'" class="txt-c--red">취소됨</span>
								<span v-else class="txt-c--blue">예약</span>
								<span v-if="current.extend_cnt > 0" class="txt-c--grey">/ {{ current.extend_cnt }}회 연장</span>
							</td>
						</tr>
						<tr v-if="current.status === 'cancel'">
							<th>취소사유</th>
							<td>{{ current.cancel_reason || '-' }}</td>
						</tr>
						<tr v-if="current.status === 'ok' && current.can_edit">
							<th>연장 종료시간</th>
							<td>
								<input type="time" class="form-control" v-model="extendTime" step="600" />
								<button type="button" class="btn btn-sm btn-secondary m-l--1" @click="submitExtend" :disabled="saving">
									연장
								</button>
							</td>
						</tr>
					</table>
					<div v-if="current.status === 'ok' && current.can_edit" class="txt-c--grey mt-1">
						※ 연장 구간에 다른 예약이 있으면 연장되지 않습니다.
					</div>
				</div>
				<div class="popup-btn">
					<button type="button" class="btn btn-light" @click="itemPopup = false">닫기</button>
					<button
						type="button"
						class="btn btn-danger"
						v-if="current.status === 'ok' && current.can_edit"
						@click="submitCancel"
						:disabled="saving"
					>
						예약 취소
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// 타임라인 슬롯: 08:00 ~ 22:00, 30분 단위. 카톡 예약이 30분 단위로 올라오므로 30분 격자면
// 대부분의 예약이 격자에 정확히 들어맞는다(경계에 안 맞는 예약도 겹치는 슬롯을 점유해 표시됨).
const SLOT_MIN = 30;
const SLOT_START = 8 * 60;
const SLOT_END = 22 * 60;
const DOW = ['일', '월', '화', '수', '목', '금', '토'];

function toMin(hhmm) {
	if (!hhmm) return null;
	const p = String(hhmm).split(':');
	return Number(p[0]) * 60 + Number(p[1] || 0);
}
function toHHMM(min) {
	const h = Math.floor(min / 60);
	const m = min % 60;
	return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default {
	name: 'BookingView',
	data() {
		const d = new Date();
		const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		const slots = [];
		for (let m = SLOT_START; m < SLOT_END; m += SLOT_MIN) slots.push(toHHMM(m));
		return {
			DOW,
			slots,
			kind: 'room',
			view: 'day',
			date: today,
			todayStr: today,
			resources: [],
			list: [],
			sdate: today,
			edate: today,
			addPopup: false,
			itemPopup: false,
			current: null,
			extendTime: '',
			saving: false,
			addForm: { resUid: null, book_date: today, stime: '', etime: '', purpose: '' },
		};
	},
	computed: {
		myUid() {
			return Number(this.$store.getters.getUserInfo.mem_uid);
		},
		myName() {
			return this.$store.getters.getUserInfo.mem_name;
		},
		kindLabel() {
			return this.kind === 'car' ? '법인차' : '회의실';
		},
		rangeLabel() {
			if (this.view === 'day') return this.date;
			return `${this.sdate} ~ ${this.edate}`;
		},
		weekDays() {
			const out = [];
			for (let i = 0; i < 7; i += 1) out.push(this.addDays(this.sdate, i));
			return out;
		},
		calWeeks() {
			const y = Number(this.date.slice(0, 4));
			const mo = Number(this.date.slice(5, 7));
			const first = new Date(y, mo - 1, 1);
			const last = new Date(y, mo, 0);
			const cells = [];
			for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
			for (let d = 1; d <= last.getDate(); d += 1) {
				cells.push(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
			}
			while (cells.length % 7 !== 0) cells.push(null);
			const weeks = [];
			for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
			return weeks;
		},
		/** (자원|일자) → 예약 배열. 타임라인/캘린더 조회를 O(1) 로 만든다. */
		byResDate() {
			const map = {};
			this.list.forEach(b => {
				const k = `${b.booking_resource_uid}|${b.book_date}`;
				if (!map[k]) map[k] = [];
				map[k].push(b);
			});
			return map;
		},
		byDate() {
			const map = {};
			this.list.forEach(b => {
				if (!map[b.book_date]) map[b.book_date] = [];
				map[b.book_date].push(b);
			});
			return map;
		},
		gridEndLabel() {
			return toHHMM(SLOT_END);
		},
		/** 일간 격자(08~22시) 어느 슬롯에도 걸치지 않는 예약 — 별도로 안내한다. */
		offGrid() {
			if (this.view !== 'day') return [];
			return this.list.filter(b => {
				const s = toMin(b.stime);
				const e = toMin(b.etime);
				return !(s < SLOT_END && e > SLOT_START);
			});
		},
	},
	created() {
		this.loadResources();
		this.load();
	},
	methods: {
		dowText(d) {
			if (!d) return '';
			const dt = new Date(`${d}T00:00:00`);
			if (Number.isNaN(dt.getTime())) return '';
			return `(${DOW[dt.getDay()]})`;
		},
		addDays(dateStr, n) {
			const dt = new Date(`${dateStr}T00:00:00`);
			dt.setDate(dt.getDate() + n);
			return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
		},
		setView(v) {
			this.view = v;
			this.load();
		},
		moveDate(delta) {
			if (this.view === 'day') this.date = this.addDays(this.date, delta);
			else if (this.view === 'week') this.date = this.addDays(this.date, delta * 7);
			else {
				const y = Number(this.date.slice(0, 4));
				const mo = Number(this.date.slice(5, 7)) + delta;
				const dt = new Date(y, mo - 1, 1);
				this.date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
			}
			this.load();
		},
		goToday() {
			this.date = this.todayStr;
			this.load();
		},
		loadResources() {
			this.$apiGET('/admin/api/booking/resource?kind=' + this.kind).then(re => {
				this.resources = Array.isArray(re) ? re : [];
			});
		},
		load() {
			this.loadResources();
			// '내 예약' 은 월 범위 + 취소 포함으로 조회한다(취소 이력 확인 R-5).
			const view = this.view === 'mine' ? 'month' : this.view;
			const q = new URLSearchParams({ view, date: this.date, kind: this.kind });
			if (this.view === 'mine') {
				q.set('memUid', String(this.myUid));
				q.set('withCancel', 'Y');
			}
			this.$apiGET('/admin/api/booking/list?' + q.toString()).then(re => {
				if (!re || !re.list) return;
				this.list = re.list;
				this.sdate = re.sdate;
				this.edate = re.edate;
			});
		},
		bookingsOf(resUid, day) {
			return this.byResDate[`${resUid}|${day}`] || [];
		},
		bookingsOfDate(day) {
			return this.byDate[day] || [];
		},
		slotRange(si) {
			const s = SLOT_START + si * SLOT_MIN;
			return [s, s + SLOT_MIN];
		},
		/** 해당 슬롯을 점유한 예약 (겹치면 먼저 시작한 것) */
		bookingAt(resUid, day, si) {
			const [s, e] = this.slotRange(si);
			const hit = this.bookingsOf(resUid, day).filter(b => toMin(b.stime) < e && toMin(b.etime) > s);
			return hit.length ? hit[0] : null;
		},
		/** 예약 블록의 시작 슬롯인지 (아니면 colspan 에 흡수되어 렌더하지 않는다) */
		coveredBySpan(resUid, day, si) {
			const b = this.bookingAt(resUid, day, si);
			if (!b) return false;
			const prev = si > 0 ? this.bookingAt(resUid, day, si - 1) : null;
			return !!prev && prev.booking_uid === b.booking_uid;
		},
		spanAt(resUid, day, si) {
			const b = this.bookingAt(resUid, day, si);
			if (!b) return 1;
			let n = 0;
			for (let i = si; i < this.slots.length; i += 1) {
				const x = this.bookingAt(resUid, day, i);
				if (!x || x.booking_uid !== b.booking_uid) break;
				n += 1;
			}
			return n || 1;
		},
		cellClass(resUid, day, si) {
			const b = this.bookingAt(resUid, day, si);
			if (!b) return 'bk-grid--free';
			return b.reg_mem_uid == this.myUid ? 'bk-grid--mine' : 'bk-grid--busy';
		},
		onCellClick(r, day, si) {
			const b = this.bookingAt(r.booking_resource_uid, day, si);
			if (b) {
				this.openItem(b);
				return;
			}
			const [s] = this.slotRange(si);
			this.openAdd(r, day, toHHMM(s), toHHMM(s + SLOT_MIN * 2));
		},
		openAdd(r, day, stime, etime) {
			this.addForm = {
				resUid: r ? r.booking_resource_uid : (this.resources.length ? this.resources[0].booking_resource_uid : null),
				book_date: day || this.date,
				stime: stime || '',
				etime: etime || '',
				purpose: '',
			};
			this.addPopup = true;
		},
		submitAdd() {
			const f = this.addForm;
			if (!f.resUid) {
				alert(`${this.kindLabel}을 선택하세요.`);
				return;
			}
			if (!f.book_date || !f.stime || !f.etime) {
				alert('예약일자와 시간을 입력하세요.');
				return;
			}
			if (f.etime <= f.stime) {
				alert('종료시간이 시작시간보다 늦어야 합니다.');
				return;
			}
			this.saving = true;
			this.$apiPOST('/admin/api/booking/add', f).then(re => {
				this.saving = false;
				// R-4: 겹침은 409 + 기존 예약자 안내 → $apiPOST 가 message 를 alert 으로 노출한다.
				if (re === false || !re || re.ok !== true) return;
				this.addPopup = false;
				this.load();
			});
		},
		openItem(b) {
			this.$apiGET('/admin/api/booking/item?id=' + b.booking_uid).then(re => {
				if (!re || !re.booking_uid) return;
				this.current = re;
				this.extendTime = re.etime;
				this.itemPopup = true;
			});
		},
		submitExtend() {
			if (!this.extendTime) {
				alert('변경할 종료시간을 입력하세요.');
				return;
			}
			if (this.extendTime <= this.current.stime) {
				alert('종료시간이 시작시간보다 늦어야 합니다.');
				return;
			}
			this.saving = true;
			this.$apiPOST('/admin/api/booking/extend', {
				bookingUid: this.current.booking_uid,
				etime: this.extendTime,
			}).then(re => {
				this.saving = false;
				if (re === false || !re || re.ok !== true) return;
				this.itemPopup = false;
				this.load();
			});
		},
		submitCancel() {
			const reason = prompt('취소 사유를 입력하세요.', '일정 변경으로 예약 취소합니다.');
			if (reason === null) return;
			this.saving = true;
			this.$apiPOST('/admin/api/booking/cancel', {
				bookingUid: this.current.booking_uid,
				reason,
			}).then(re => {
				this.saving = false;
				if (re === false || !re || re.ok !== true) return;
				this.itemPopup = false;
				this.load();
			});
		},
	},
};
</script>

<style lang="scss">
.bk-scroll {
	overflow-x: auto;
}
.bk-grid {
	table-layout: fixed;
	min-width: 1100px;
	td,
	th {
		font-size: 11px;
		padding: 2px !important;
	}
	&--head {
		border-left: 1px solid #e5e7eb;
	}
	&--free {
		cursor: pointer;
		&:hover {
			background: #eef2ff;
		}
	}
	&--busy {
		background: #e5e7eb;
		cursor: pointer;
	}
	&--mine {
		background: #cfe2ff;
		cursor: pointer;
	}
	&--label {
		display: block;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		text-align: left;
	}
}
.bk-week {
	table-layout: fixed;
	&--cell {
		vertical-align: top !important;
		text-align: left !important;
		height: 80px;
	}
	&--ev {
		display: block;
		width: 100%;
		text-align: left;
		margin-bottom: 2px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
	&--add {
		width: 100%;
	}
}
.bk-today {
	background: #eef2ff;
}
.bk-cal {
	table-layout: fixed;
	&--cell {
		height: 96px;
		vertical-align: top !important;
		text-align: left !important;
		padding: 4px !important;
	}
	&--day {
		font-weight: 600;
		margin-bottom: 2px;
		&.today span {
			background: #0d6efd;
			color: #fff;
			border-radius: 50%;
			padding: 1px 6px;
		}
	}
	&--ev {
		display: block;
		width: 100%;
		text-align: left;
		margin-bottom: 2px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
}
</style>
